import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { AppType, ENVIRONMENT, Environment, ErrorCode, LoginModel } from '@domains';
import { encryptKey, LocalStorageService } from '@rspl-api';
import {
  CardComponent,
  Destructible,
  DisableDoubleClickDirective,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';
import * as CryptoJS from 'crypto-js';
import { filter, finalize, take } from 'rxjs/operators';

import { AFTER_LOGIN, AuthenticationService } from '../../authentication.service';
import { loadGoogleSignIn, renderGoogleSignInButton } from '../../google-sign-in';

@Component({
  selector: 'rspl-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgTemplateOutlet,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    FxLayoutGapDirective,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatIconButton,
    MatSuffix,
    MatIcon,
    LaddaModule,
    CardComponent,
    FxFlexDirective,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
    MatButton,
    DisableDoubleClickDirective,
    RouterLink,
  ],
})
export class LoginComponent extends Destructible implements OnInit, AfterViewInit {
  public form?: FormGroup<{
    email: FormControl<string | null>;
    password: FormControl<string | null>;
  }>;
  public app: AppType;
  public resetPasswordUrl: string;
  appTypes = AppType;
  isTPL: boolean;
  isDriver: boolean;
  isGoogleSignIn: boolean;
  // captain only: the email/password form is folded away behind a link until asked for. The Zendesk build
  // runs inside Zendesk's iframe, where the Google popup is at the host's mercy, so it starts unfolded.
  showCredentials: boolean;
  // where Google draws its button once the GIS script is up (captain only; the div is in the template)
  @ViewChild('googleButton') googleButton?: ElementRef<HTMLElement>;
  // only set when Google's script failed to load or its button could not be drawn (the host stays empty); sign-in errors go to the snackbar
  googleSignInError?: string;
  support?: string;
  showPassword = false;
  isSubmitting = false;
  // which button started the sign-in, so only that one shows the spinner
  submittingVia?: 'email' | 'google';
  showErrors = false;

  constructor(
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private authService: AuthenticationService,
    private snackBar: MatSnackBar,
    private router: Router,
    @Inject(ENVIRONMENT) private config: Environment,
    private localStorage: LocalStorageService,
    private zone: NgZone,
  ) {
    super();
    this.app = this.config.app === AppType.ZENDESK ? AppType.CAPTAIN : this.config.app;
    this.isTPL = this.config.app === AppType.TPL;
    this.isDriver = this.config.app === AppType.DRIVER;
    // Captain (and the Zendesk captain build) signs in with Google first; the classic form stays one link away
    this.isGoogleSignIn = this.app === AppType.CAPTAIN;
    this.showCredentials = this.config.app === AppType.ZENDESK;
    this.support = this.config.supportNumber;
    this.resetPasswordUrl = this.config.urls.resetPasswordUrl;

    iconRegistry.addSvgIcon('rspl-logo', sanitizer.bypassSecurityTrustResourceUrl('assets/images/new_logo_short.svg'));
  }

  ngOnInit(): void {
    const afterLogin = this.localStorage.getItem(AFTER_LOGIN);
    const shouldSkipReload = !!afterLogin; // Skip reload if we have a redirect target

    if (!this.localStorage.getItem('LOGIN_FRESH') && !shouldSkipReload) {
      this.localStorage.setItem('LOGIN_FRESH', 'true');
      window.location.reload();
      return;
    }

    this.localStorage.removeItem('LOGIN_FRESH');

    document.getElementById('app-page-loader')?.remove();
    sessionStorage.clear();

    // Only clear localStorage if user is not already logged in
    const userData = this.localStorage.getItem('rspl-user');
    if (!userData) {
      this.localStorage.clear();
      if (afterLogin) {
        this.localStorage.setItem(AFTER_LOGIN, afterLogin);
      }
    }
    this.initForm();
  }

  // the Google button needs its host element, which exists only once the view is built
  ngAfterViewInit(): void {
    if (this.isGoogleSignIn) {
      this.initGoogleSignIn();
    }
  }

  public login(): void {
    if (this.isSubmitting) return;
    this.form?.updateValueAndValidity();
    this.form?.markAllAsTouched();
    this.showErrors = !!this.form?.invalid;
    if (this.showErrors) {
      return;
    }
    const formValue = this.form?.getRawValue();
    if (!formValue?.password || !formValue.email) return;
    this.isSubmitting = true;
    this.submittingVia = 'email';
    const user = new LoginModel({
      email: formValue?.email,
      password: formValue?.password,
    });
    this.signIn(user, () => this.handleError('Access Not Allowed'));
  }

  // Google's button hands over a signed ID token once an account is picked; the API verifies it.
  // GIS keeps the callback in global state, so a popup finished after this component was torn down
  // (route changed under it) must not act on the dead instance; the row is also blocked while any
  // sign-in request runs, so a second request can't start.
  private loginWithGoogle(idToken: string): void {
    if (this.destroyed || this.isSubmitting) return;
    this.isSubmitting = true;
    this.submittingVia = 'google';
    this.snackBar.dismiss();
    const user = new LoginModel({
      provider: 'google',
      idToken,
    });
    this.signIn(user, (error) => this.handleError(this.googleSignInMessage(error)));
  }

  // Shown in the same snackbar as a wrong email/password. Only a refused account reads as "no access"
  // (no google captain with that email); Google's own hiccups and an unreachable API say so instead.
  private googleSignInMessage(error: unknown): string {
    const response = error as HttpErrorResponse;
    switch (response?.error?.code) {
      case ErrorCode.google_user_not_found_error:
        return 'This Google account has no captain access';
      case ErrorCode.google_id_token_error:
        return "Google sign-in didn't go through. Please try again.";
      default:
        return response?.status >= 500 || response?.status === 0
          ? "We couldn't reach the server. Please try again."
          : 'Sign-in failed. Please try again.';
    }
  }

  private signIn(user: LoginModel, onError: (error: unknown) => void): void {
    this.authService
      .login(user)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.submittingVia = undefined;
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          const afterLoginLocalStorage = this.localStorage.getItem(AFTER_LOGIN);

          let targetPath = this.config.loginRedirectionRoute;
          let queryParams: any = {};

          if (this.config.dynamicAfterLoginRedirect && afterLoginLocalStorage) {
            try {
              // Decrypt if production
              const decryptedUrl =
                this.config.production && afterLoginLocalStorage.includes('U2FsdGVk')
                  ? this.decryptUrl(afterLoginLocalStorage)
                  : afterLoginLocalStorage;

              if (decryptedUrl && decryptedUrl !== '/' && !decryptedUrl.includes('login')) {
                const parts = decryptedUrl.split('?');
                const afterLoginUrl = parts[0];
                const params = parts.length > 1 ? parts[1].split('&') : undefined;

                queryParams =
                  params?.reduce((a, v) => {
                    const p = v.split('=');
                    return { ...a, [p[0]]: decodeURIComponent(p[1] || '') };
                  }, {}) || {};

                const afterLogin = afterLoginUrl.split('/').filter((x) => x.trim().length > 0);

                if (afterLogin.length > 0) {
                  targetPath = afterLogin[0] === '' ? afterLogin.slice(1) : afterLogin;
                  if (targetPath.length === 0) {
                    targetPath = this.config.loginRedirectionRoute;
                  } else {
                    targetPath = ['/', ...targetPath];
                  }
                }
              }
            } catch (error) {
              // Fallback to default on error
            }
          }

          // Wait for auth state to be fully established before navigating
          this.authService.isLoggedIn$
            .pipe(
              filter((isLoggedIn) => isLoggedIn === true),
              take(1),
            )
            .subscribe(() => {
              // Small delay to ensure all state is synchronized
              setTimeout(() => {
                this.router.navigate(targetPath, { queryParams: queryParams }).then((success) => {
                  if (success) {
                    // Clean up AFTER_LOGIN only after successful navigation
                    this.localStorage.removeItem(AFTER_LOGIN);
                  }
                });
              }, 100);
            });
        },
        error: onError,
      });
  }

  private initGoogleSignIn(): void {
    loadGoogleSignIn().then(
      () => {
        if (this.destroyed) return;
        const host = this.googleButton?.nativeElement;
        if (!host) {
          this.googleSignInError = "Couldn't show Google sign-in. Please refresh the page.";
          return;
        }
        renderGoogleSignInButton(host, this.config.googleClientId, (idToken) => this.zone.run(() => this.loginWithGoogle(idToken)));
      },
      () => (this.googleSignInError = "Couldn't load Google sign-in. Please refresh the page."),
    );
  }

  private initForm(): void {
    this.form = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
    });
  }

  handleError(message: string) {
    this.snackBar.open(message, 'x', {
      duration: 5000,
      panelClass: 'error',
    });
  }

  get email(): FormControl<string> {
    return this.form?.get('email') as FormControl<string>;
  }

  get password(): FormControl<string> {
    return this.form?.get('password') as FormControl<string>;
  }

  private decryptUrl(encryptedUrl: string): string {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedUrl, encryptKey, {
        keySize: 128 / 8,
        iv: encryptKey,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.warn('[LOGIN] Failed to decrypt URL:', error);
      return '';
    }
  }
}
